import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { offersEndpoint } from "../constants";

const UserOffers = () => {
  const [offers, setOffers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${offersEndpoint}user/`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        
        // Ensure we always set an array
        const offersData = response.data.results || response.data;
        setOffers(Array.isArray(offersData) ? offersData : []);
      } catch (err) {
        setError("Failed to load offers.");
        console.error(err);
        setOffers([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, [token]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const toggleDropdown = (offerId) => {
    setDropdownOpen(dropdownOpen === offerId ? null : offerId);
  };

  const handleOfferAction = async (offerId, action) => {
    try {
      await axios.patch(`${offersEndpoint}${offerId}/`, {
        status: action
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        }
      });
      
      // Refresh offers list
      const response = await axios.get(`${offersEndpoint}user/`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        }
      });
      
      const offersData = response.data.results || response.data;
      setOffers(Array.isArray(offersData) ? offersData : []);
      setDropdownOpen(null);
    } catch (err) {
      console.error("Failed to update offer:", err);
      setError("Failed to update offer status.");
    }
  };

  const filteredOffers = useMemo(() => {
    // Ensure offers is always an array
    if (!Array.isArray(offers)) {
      return [];
    }

    let result = [...offers];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (offer) =>
          offer.job?.title?.toLowerCase().includes(searchLower) ||
          offer.job?.company?.toLowerCase().includes(searchLower) ||
          offer.service?.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter) {
      result = result.filter(offer => offer.status === statusFilter);
    }

    return result;
  }, [offers, searchTerm, statusFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setDropdownOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return "N/A";
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-600 border-yellow-400',
      accepted: 'bg-green-100 text-green-600 border-green-400',
      rejected: 'bg-red-100 text-red-600 border-red-400',
      withdrawn: 'bg-gray-100 text-gray-600 border-gray-400'
    };

    return statusClasses[status] || 'bg-gray-100 text-gray-600 border-gray-400';
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return "N/A";
    }
  };

  // Ensure filteredOffers is always an array
  const safeFilteredOffers = Array.isArray(filteredOffers) ? filteredOffers : [];

  return (
    <div className="lg:p-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">My Job Offers</h1>
          <p className="mt-1 text-sm text-gray-600">
            View and manage job offers you've received.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/candidate/applications")}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            My Applications
          </button>
        </div>
      </div>

      {/* Loading/Error */}
      {loading && <p className="text-gray-500">Loading offers...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {/* Offers Table */}
      {!loading && !error && (
        <div className="bg-white rounded-xl border border-gray-200 mt-10">
          <div className="pt-3 px-5">
            <h3 className="text-sm font-semibold text-gray-900">Total Offers</h3>
          </div>
          <div className="flex gap-3 items-end py-4 px-5 border-b border-gray-200">
            <h3 className="text-2xl text-gray-900 font-semibold">
              {safeFilteredOffers.length}
            </h3>
            <div className="flex items-center gap-1 text-sm text-emerald-600">
              <span>🎉</span>
              <span className="text-gray-600 ml-1">Congratulations on your offers!</span>
            </div>
          </div>

          {/* Filters */}
          <div className="px-5 py-4 border-b border-gray-200">
            <div className="flex gap-4 items-center">
              <input
                type="text"
                placeholder="Search offers..."
                value={searchTerm}
                onChange={handleSearch}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              />
              <select
                value={statusFilter}
                onChange={handleStatusFilter}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="withdrawn">Withdrawn</option>
              </select>
            </div>
          </div>

          <div>
            <div className="px-5 py-3 grid md:grid-cols-8 grid-cols-5 border-b border-gray-200">
              <div className="flex gap-3 items-center md:col-span-3 col-span-2">
                <input type="checkbox" className="rounded border-gray-300" />
                <h5 className="text-gray-500 font-semibold">Position</h5>
              </div>
              <div className="grid md:grid-cols-5 grid-cols-3 md:col-span-5 col-span-3">
                <h5 className="text-gray-500 font-semibold text-xs">Company</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">Salary</h5>
                <h5 className="text-gray-500 font-semibold text-xs">Service Type</h5>
                <h5 className="text-gray-500 font-semibold text-xs">Status</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">Date Received</h5>
              </div>
            </div>

            {safeFilteredOffers.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-gray-400 text-6xl mb-4">📋</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No offers yet</h3>
                <p className="text-gray-600">Keep applying to jobs to receive offers!</p>
              </div>
            ) : (
              safeFilteredOffers.map((offer) => (
                <div key={offer.id} className="grid md:grid-cols-8 grid-cols-5 items-center p-4 border-b border-gray-200">
                  <div className="flex items-center gap-3 md:col-span-3 col-span-2">
                    <input type="checkbox" className="rounded border-gray-300" />
                    <div>
                      <p className="md:font-medium font-bold text-sm text-gray-900">
                        {offer.job?.title || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500 md:hidden">
                        {offer.job?.company || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-xs text-gray-500 hidden md:block">
                      {offer.job?.company || 'N/A'}
                    </p>
                  </div>
                  <div className="hidden md:block">
                    <p className="font-semibold text-sm text-gray-900">
                      {formatCurrency(offer.income)}
                    </p>
                    <p className="text-xs text-gray-500">per year</p>
                  </div>
                  <div>
                    <span className="text-xs bg-blue-100 text-blue-600 border border-blue-400 py-1 px-2 rounded-md capitalize">
                      {offer.service?.replace('_', ' ') || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center w-full">
                    <span className={`text-center text-[12px] border py-1 px-2 rounded-md capitalize ${getStatusBadge(offer.status)}`}>
                      {offer.status || 'Pending'}
                    </span>
                    <div className="relative md:hidden">
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdown(offer.id);
                        }}
                        className="text-gray-600 cursor-pointer text-xl font-bold"
                      >
                        &#8942;
                      </span>
                      {dropdownOpen === offer.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                          <div className="py-1">
                            <button
                              onClick={() => navigate(`/offer_details/${offer.id}`)}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              View Details
                            </button>
                            {offer.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleOfferAction(offer.id, 'accepted')}
                                  className="block w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                >
                                  Accept Offer
                                </button>
                                <button
                                  onClick={() => handleOfferAction(offer.id, 'rejected')}
                                  className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                >
                                  Reject Offer
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="hidden md:flex justify-between items-center">
                    <span className="font-medium text-xs text-gray-500">
                      {formatDate(offer.date_published)}
                    </span>
                    <div className="relative">
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdown(offer.id);
                        }}
                        className="text-gray-600 cursor-pointer text-xl font-bold xl:ml-10"
                      >
                        &#8942;
                      </span>
                      {dropdownOpen === offer.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                          <div className="py-1">
                            <button
                              onClick={() => navigate(`/offer_details/${offer.id}`)}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              View Details
                            </button>
                            {offer.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleOfferAction(offer.id, 'accepted')}
                                  className="block w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                >
                                  Accept Offer
                                </button>
                                <button
                                  onClick={() => handleOfferAction(offer.id, 'rejected')}
                                  className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                >
                                  Reject Offer
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Summary Cards for Pending Offers */}
          {safeFilteredOffers.filter(offer => offer.status === 'pending').length > 0 && (
            <div className="p-5 bg-yellow-50 border-t border-yellow-200">
              <div className="flex items-center gap-2 text-yellow-800">
                <span>⚠️</span>
                <p className="text-sm font-medium">
                  You have {safeFilteredOffers.filter(offer => offer.status === 'pending').length} pending offer(s) waiting for your response.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserOffers;